import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Helmet } from 'react-helmet';
import RegistrationForm from './components/RegistrationForm';
import SocialRegistration from './components/SocialRegistration';
import TrustSignals from './components/TrustSignals';
import SubscriptionPreview from './components/SubscriptionPreview';
import SubscriptionCheckout from '../../components/SubscriptionCheckout';
import SiteMessageContainer from '../../components/SiteMessageContainer';
import studioApplicationService from '../../utils/studioApplicationService';

const UserRegistration = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('registration'); // 'registration' or 'subscription'
  const [registrationData, setRegistrationData] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { signUp, signInWithOAuth, user, authError } = useAuth();

  // Get return path from state
  const returnTo = location.state?.returnTo;
  const gameTitle = location.state?.gameTitle;
  const gameTier = location.state?.tier;

  useEffect(() => {
    // Check if user is already logged in
    if (user) {
      // If there's a return path, go there, otherwise go to dashboard
      navigate(returnTo || '/game-library-dashboard');
    }
  }, [user, navigate, returnTo]);

  // Update local error when auth error changes
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  const handleRegistration = async (formData) => {
    setIsLoading(true);
    setError('');

    try {
      const userData = {
        full_name: formData.displayName || formData.email.split('@')[0],
        preferred_plan: formData.accountType === 'studio' ? 'free' : formData.accountType
      };

      const result = await signUp(formData.email, formData.password, userData);

      if (result.success) {
        // If studio account, store data and redirect to application form
        // The user will submit the application after the auth session is established
        if (formData.accountType === 'studio') {
          // Store studio application data in session storage
          sessionStorage.setItem('pendingStudioApplication', JSON.stringify({
            studioName: formData.studioName,
            studioWebsite: formData.studioWebsite,
            studioDescription: formData.studioDescription,
            teamSize: formData.teamSize
          }));

          // Redirect to application form which will auto-submit
          navigate('/studio-application-form?autoSubmit=true');
          return;
        }

        // For free accounts, go directly to return path or dashboard
        if (formData.accountType === 'free') {
          navigate(returnTo || '/game-library-dashboard');
        } else {
          // For premium accounts, store registration data and move to subscription step
          setRegistrationData({ formData, userData });
          setStep('subscription');
        }
      } else {
        setError(result.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError('Registration failed. Please try again.');
      console.log('Registration error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialRegistration = async (provider) => {
    setIsLoading(true);
    setError('');

    try {
      const result = await signInWithOAuth(provider.toLowerCase());
      
      if (result.success) {
        // For OAuth, we'll handle subscription setup in the auth callback
        // The redirect will be handled by Supabase
      } else {
        setError(result.error || `${provider} registration failed. Please try again.`);
      }
    } catch (err) {
      setError(`${provider} registration failed. Please try again.`);
      console.log(`${provider} registration error:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscriptionSuccess = (subscriptionResult) => {
    console.log('Subscription created successfully:', subscriptionResult);
    // Navigate to return path or dashboard after successful subscription
    navigate(returnTo || '/game-library-dashboard');
  };

  const handleSubscriptionError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleBackToRegistration = () => {
    setStep('registration');
    setRegistrationData(null);
    setError('');
  };

  return (
    <>
      <Helmet>
        <title>Join Solis Games - Unlimited Gaming Access</title>
        <meta name="description" content="Join Solis Games and get unlimited access to hundreds of WebGPU games for just $5/month. Start your gaming journey today!" />
        <meta name="keywords" content="game streaming, WebGPU, gaming subscription, unlimited games" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center p-4">
        {/* Background Gaming Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-32 h-32 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-secondary/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-accent/5 rounded-full blur-2xl animate-pulse"></div>
        </div>

        {step === 'registration' ? (
          // Registration Step
          <div className="relative w-full max-w-4xl">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Left Column - Registration Form */}
              <div className="bg-card/50 backdrop-blur-sm border border-border rounded-2xl shadow-gaming-xl p-8">
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-primary mb-2">
                    Join Solis Games
                  </h1>
                  <p className="text-muted-foreground">
                    Unlimited access to hundreds of WebGPU games
                  </p>
                </div>

                {/* Site Messages */}
                <SiteMessageContainer page="registration" variant="full" className="mb-6" />

                {error && (
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-destructive">{error}</span>
                    </div>
                  </div>
                )}

                <div className="space-y-6">
                  <SocialRegistration
                    onSocialRegister={handleSocialRegistration}
                    isLoading={isLoading}
                  />
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="bg-card px-4 text-muted-foreground">Or register with email</span>
                    </div>
                  </div>
                  
                  <RegistrationForm
                    onSubmit={handleRegistration}
                    isLoading={isLoading}
                    error={error}
                  />
                </div>
                
                <TrustSignals />
              </div>

              {/* Right Column - Subscription Preview */}
              <div className="space-y-6">
                <SubscriptionPreview />
                
                {/* Features Preview */}
                <div className="bg-card/30 backdrop-blur-sm border border-border rounded-2xl p-6">
                  <h3 className="text-xl font-semibold mb-4">What you get with Solis Games</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      </div>
                      <span className="text-sm">Unlimited access to 500+ WebGPU games</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      </div>
                      <span className="text-sm">New games added weekly</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      </div>
                      <span className="text-sm">Play across all devices</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      </div>
                      <span className="text-sm">Cancel anytime</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="text-center mt-8">
              <p className="text-xs text-muted-foreground">
                Already have an account? <a href="/user-login" className="text-primary hover:underline">Sign in here</a>
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                By signing up, you agree to our Terms of Service and Privacy Policy
              </p>
            </div>
          </div>
        ) : (
          // Subscription Step
          <div className="relative w-full max-w-2xl">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-primary mb-2">
                Welcome to Solis Games!
              </h1>
              <p className="text-muted-foreground">
                Complete your subscription to start playing
              </p>
            </div>

            <SubscriptionCheckout
              onSuccess={handleSubscriptionSuccess}
              onError={handleSubscriptionError}
              onCancel={handleBackToRegistration}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default UserRegistration;
